import React, { useState, useEffect } from 'react';
import { X, Trash2 } from 'lucide-react';
import { formatDateKey } from '../constants';

// Set / edit / clear the OSHA baseline date. Manager-gated (parent only renders
// this for users with safety_calendar_manage).
export default function BaselineModal({ initial, onSave, onClear, onClose }) {
  const [date, setDate] = useState(initial || '');
  const [confirmClear, setConfirmClear] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const todayKey = formatDateKey(new Date());

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (!confirmClear) return;
    const t = setTimeout(() => setConfirmClear(false), 4000);
    return () => clearTimeout(t);
  }, [confirmClear]);

  const canSave = date && date <= todayKey && !saving;

  const run = async (fn) => {
    setSaving(true); setError('');
    try { await fn(); } catch (e) { setError(e?.message || 'Action failed.'); setSaving(false); setConfirmClear(false); }
  };

  const inputCls = 'w-full bg-slate-900 border border-slate-600 rounded-md px-3 py-2 text-white focus:border-blue-500 outline-none';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Scoreboard</div>
            <h3 className="text-lg font-semibold text-white mt-0.5">OSHA baseline date</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-700 text-slate-300"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-300 leading-relaxed">
            Set this to the date of your last OSHA reportable injury that happened before this
            calendar started tracking. The counter uses this date until a more recent OSHA
            reportable injury is logged — then the logged entry takes over automatically.
          </p>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Date of last OSHA reportable injury</label>
            <input type="date" value={date} max={todayKey} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          </div>
          {error && <div className="text-sm text-red-400">{error}</div>}
        </div>
        <div className="border-t border-slate-700 px-6 py-4 flex items-center justify-between">
          <div>
            {initial && (
              <button
                onClick={() => (confirmClear ? run(onClear) : setConfirmClear(true))}
                className={`text-sm px-3 py-2 rounded-md border flex items-center gap-2 transition-colors ${
                  confirmClear ? 'border-red-600 bg-red-600 text-white hover:bg-red-700' : 'border-red-600 text-red-400 hover:bg-red-600 hover:text-white'
                }`}
              >
                <Trash2 className="w-4 h-4" /> {confirmClear ? 'Confirm Clear' : 'Clear'}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-sm px-4 py-2 rounded-md border border-slate-600 text-slate-300 hover:bg-slate-700">Cancel</button>
            <button
              onClick={() => run(() => onSave(date))}
              disabled={!canSave}
              className={`text-sm px-4 py-2 rounded-md text-white ${canSave ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-600 cursor-not-allowed opacity-60'}`}
            >
              {saving ? 'Saving...' : 'Save Baseline'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
